import React, { useState } from "react";
import { api, upload } from "./api";
import { RESCUE_LOCATION_LABEL, planHref, type AllergyPanelData } from "./AllergyBanner";
import { useProblems, FieldProblem, invalid } from "./form-check";
import { allergyResponseRules } from "./checks";

/**
 * The allergy response (M3 Stage 3, owner decision 2026-10-16), on the record
 * review screen and the med-station visit: the latest note (append-only:
 * writing again adds a new one and the earlier stays in the history), the
 * form for the RN or a Supervisor, and attaching the family's allergy /
 * anaphylaxis action plan (RN and up). The note then shows in the allergy
 * banner on every screen that shows allergies.
 */

export interface AllergyResponseOptions {
  standard: string;
  canWrite: boolean;
  canAttachPlan: boolean;
  severities: string[];
  rescueLocations: string[];
  /** The rescue medication recorded as on the student's person, if any: "where it is" starts at Self-carry. */
  rescueOnPerson?: string | null;
}

export function AllergyResponseCard({ personKey, panel, options, onPanel }: { personKey: string; panel: AllergyPanelData; options: AllergyResponseOptions; onPanel: (p: AllergyPanelData) => void }): React.ReactElement | null {
  const r = panel.response ?? null;
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState(() => {
    const rescueMedication = r?.rescueMedication || options.rescueOnPerson || ((panel.confirmed?.rows ?? panel.allergies).some((a) => a.bringingEpiPen === "Yes") ? "Epinephrine auto-injector" : "");
    return {
    allergens: r?.allergens ?? [...(panel.confirmed?.rows ?? panel.allergies).map((a) => a.allergy)].join(", "),
    reactionHistory: r?.reactionHistory ?? (panel.confirmed?.rows ?? panel.allergies).map((a) => a.reaction).filter(Boolean).join("; "),
    severity: r?.severity ?? ((panel.confirmed?.rows ?? panel.allergies).some((a) => a.anaphylaxisRisk === "Yes") ? "Anaphylaxis" : ""),
    rescueMedication,
    // On the student's person: Self-carry, unless an earlier note said where.
    rescueLocation: r?.rescueLocation || (rescueMedication && options.rescueOnPerson ? "SelfCarry" : ""),
    steps: r?.steps ?? options.standard,
    whoToCall: r?.whoToCall ?? "",
    };
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plan, setPlan] = useState<File | null>(null);
  const set = (k: keyof typeof f, v: string) => setF({ ...f, [k]: v });
  const { problems, check } = useProblems(allergyResponseRules(f));
  const P = ({ k }: { k: string }) => <FieldProblem id={k} problems={problems} />;
  const listed = panel.allergies.length > 0 || Boolean(panel.confirmed?.rows.length) || Boolean(r);
  if (!listed && !options.canWrite) return null;

  async function save() {
    setError(null);
    if (!check()) return;
    setBusy(true);
    const x = await api<{ allergyBanner: AllergyPanelData }>("POST", `/api/records/${encodeURIComponent(personKey)}/allergy-response`, f);
    setBusy(false);
    if (!x.ok) return setError(x.data.error ?? "Not saved.");
    setEditing(false);
    onPanel(x.data.allergyBanner);
  }
  async function attach() {
    if (!plan) return;
    setError(null);
    const x = await upload<{ allergyBanner: AllergyPanelData }>(`/api/records/${encodeURIComponent(personKey)}/allergy-plan?name=${encodeURIComponent(plan.name)}`, plan);
    if (!x.ok) return setError(x.data.error ?? "The plan didn't upload.");
    setPlan(null);
    onPanel(x.data.allergyBanner);
  }

  return (
    <section className="section-card border-l-4 border-l-red-600">
      <h3 className="mb-1 text-base font-semibold">Allergy response</h3>
      {r ? (
        <div className="text-sm">
          <p><span className="font-medium">Severity:</span> {r.severity} · <span className="font-medium">Allergens:</span> {r.allergens}</p>
          {r.reactionHistory && <p><span className="font-medium">Reaction history:</span> {r.reactionHistory}</p>}
          <p><span className="font-medium">Rescue medication:</span> {r.rescueMedication || "none"}{r.rescueLocation ? ` — ${RESCUE_LOCATION_LABEL[r.rescueLocation] ?? r.rescueLocation}` : ""}</p>
          <p className="whitespace-pre-wrap"><span className="font-medium">If exposed:</span> {r.steps}</p>
          {r.whoToCall && <p><span className="font-medium">Who to call:</span> {r.whoToCall}</p>}
          <p className="text-xs text-slate-500">By {r.authorName || r.authorEmail}{r.earlier ? ` · ${r.earlier} earlier version${r.earlier === 1 ? "" : "s"} kept` : ""}</p>
        </div>
      ) : (
        <p className="text-sm text-slate-600">No allergy response note yet.</p>
      )}
      <p className="mt-2 text-sm">
        Action plan: {panel.plans?.length ? <a className="text-program underline" href={planHref(panel.plans[0])} target="_blank" rel="noreferrer">on file{panel.plans[0].source === "portal" ? " (the family's Step 3 upload)" : ""}</a> : "none on file"}
      </p>
      {options.canAttachPlan && (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <input type="file" accept="application/pdf,image/*" className="text-sm" onChange={(e) => setPlan(e.target.files?.[0] ?? null)} />
          <button type="button" className="btn-secondary" disabled={!plan} onClick={attach}>Attach the family's action plan</button>
        </div>
      )}
      {options.canWrite && !editing && <button type="button" className="btn-secondary mt-3" onClick={() => setEditing(true)}>{r ? "Write a new version" : "Write the allergy response"}</button>}
      {editing && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div><label htmlFor="ar-a" className="field-label">Allergen(s)</label><input id="ar-a" className="field-input" {...invalid("ar-a", problems)} value={f.allergens} onChange={(e) => set("allergens", e.target.value)} /><P k="ar-a" /></div>
          <div>
            <label htmlFor="ar-s" className="field-label">Severity</label>
            <select id="ar-s" className="field-input" {...invalid("ar-s", problems)} value={f.severity} onChange={(e) => set("severity", e.target.value)}><option value="">Choose…</option>{options.severities.map((s) => <option key={s} value={s}>{s}</option>)}</select>
            <P k="ar-s" />
          </div>
          <div className="sm:col-span-2"><label htmlFor="ar-h" className="field-label">Reaction history</label><input id="ar-h" className="field-input" value={f.reactionHistory} onChange={(e) => set("reactionHistory", e.target.value)} /></div>
          <div><label htmlFor="ar-m" className="field-label">Rescue medication on hand</label><input id="ar-m" className="field-input" value={f.rescueMedication} onChange={(e) => set("rescueMedication", e.target.value)} placeholder="e.g. Epinephrine auto-injector 0.3 mg" /></div>
          <div>
            <label htmlFor="ar-l" className="field-label">Where it is</label>
            <select id="ar-l" className="field-input" {...invalid("ar-l", problems)} value={f.rescueLocation} onChange={(e) => set("rescueLocation", e.target.value)}><option value="">—</option>{options.rescueLocations.map((l) => <option key={l} value={l}>{RESCUE_LOCATION_LABEL[l] ?? l}</option>)}</select>
            <P k="ar-l" />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="ar-st" className="field-label">Steps if exposed (program standard; edit, or copy from the provider's plan)</label>
            <textarea id="ar-st" className="field-input min-h-28" {...invalid("ar-st", problems)} value={f.steps} onChange={(e) => set("steps", e.target.value)} />
            <P k="ar-st" />
          </div>
          <div className="sm:col-span-2"><label htmlFor="ar-c" className="field-label">Who to call</label><input id="ar-c" className="field-input" value={f.whoToCall} onChange={(e) => set("whoToCall", e.target.value)} placeholder="e.g. 911, then the med station and the parent" /></div>
          <div className="flex flex-wrap gap-2 sm:col-span-2">
            <button type="button" className="btn-primary" disabled={busy} onClick={save}>Save the allergy response</button>
            <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
      {error && <p className="field-error" role="alert">{error}</p>}
    </section>
  );
}
